import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "../lib/api";
import type { CurrentUser } from "../lib/types";

/**
 * Sesja użytkownika HQ.
 *
 * `401` nie jest tu błędem, tylko odpowiedzią „nie zalogowany" — dlatego
 * zapytanie o `me` nie ponawia się i nie krzyczy w konsoli. Każdy inny błąd
 * jest błędem i ma prawo hałasować.
 */
export function useSession() {
  return useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        const data = await api.get<{ user: CurrentUser }>("/auth/me");
        return data.user;
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthorized) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      api.post<{ user: CurrentUser }>("/auth/login", credentials),
    onSuccess: (data) => {
      queryClient.setQueryData(["session"], data.user);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSuccess: () => {
      queryClient.clear();
      queryClient.setQueryData(["session"], null);
    },
  });
}
